import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { apiCall, ENDPOINTS } from '../config/api';
import { User, AuthState, LoginCredentials, RegisterData } from '../types';

// Secure token storage class - uses sessionStorage so the session dies with
// the browser/tab rather than surviving a restart. Persisting auth across a
// full browser close belongs to "remember me"-style features, not a bare
// token; sessionStorage still survives reloads/navigation within the same
// tab, which is all normal use needs.
class SecureStorage {
  static setToken(token: string): void {
    sessionStorage.setItem('auth_token', token);
    sessionStorage.setItem('session_start_time', Date.now().toString());
  }

  static getToken() {
    return sessionStorage.getItem('auth_token');
  }

  static setUser(user: User): void {
    sessionStorage.setItem('user_data', JSON.stringify(user));
  }

  static getUser() {
    const userData = sessionStorage.getItem('user_data');
    return userData ? JSON.parse(userData) : null;
  }

  static getSessionStartTime() {
    const startTime = sessionStorage.getItem('session_start_time');
    return startTime ? parseInt(startTime) : null;
  }

  static clearSession() {
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user_data');
    sessionStorage.removeItem('session_start_time');
  }
}

// Exported so call sites that need the raw stored user outside of useAuth()
// read it through the same mechanism instead of hard-coding localStorage/
// sessionStorage and drifting out of sync with where it actually lives.
export const getStoredUser = () => SecureStorage.getUser();

// Auth context type definition
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionTimeRemaining: number;
  sessionLocked: boolean;
  login: (authToken: string, userData: User) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
  resetSessionTimer: () => void;
  lockScreen: () => void;
}

// Create the auth context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number>(1800); // 30 minutes in seconds
  const [sessionLocked, setSessionLocked] = useState<boolean>(false);
  const sessionTimeoutRef = useRef<number | null>(null);
  const countdownRef = useRef<number | null>(null);

  // Locks the screen in place rather than logging out -- the token/user stay
  // in state so mounted work (an open form, an in-progress upload) survives
  // behind the overlay. Only an explicit logout() actually clears the
  // session. Matches Clinical Outcomes' screen-lock behavior.
  const lockScreen = useCallback(() => {
    setSessionLocked(true);
  }, []);

  // Reset session timer - called on any user activity
  const resetSessionTimer = useCallback(() => {
    if (!token) return;

    // Clear existing timers
    if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
    if (countdownRef.current) window.clearInterval(countdownRef.current);

    // Reset countdown to 30 minutes
    setSessionTimeRemaining(1800);

    // Start countdown timer (updates every second)
    countdownRef.current = window.setInterval(() => {
      setSessionTimeRemaining(prev => {
        if (prev <= 1) {
          // Time's up - lock the screen (not a hard logout; see lockScreen above)
          lockScreen();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Set main session timeout (30 minutes)
    sessionTimeoutRef.current = window.setTimeout(() => {
      lockScreen();
    }, 1800000); // 30 minutes
  }, [token, lockScreen]);

  const login = (authToken: string, userData: User) => {
    setToken(authToken);
    setUser(userData);
    setSessionLocked(false);
    SecureStorage.setToken(authToken);
    SecureStorage.setUser(userData);
    resetSessionTimer();
  };

  const logout = async () => {
    try {
      // Call logout API to invalidate token
      if (token) {
        await apiCall(ENDPOINTS.AUTH.LOGOUT, {
          method: 'POST',
        }, token);
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local state regardless of API call success
      setToken(null);
      setUser(null);
      setSessionLocked(false);
      SecureStorage.clearSession();
    }
  };

  const checkAuth = async () => {
    const storedToken = SecureStorage.getToken();
    const storedUser = SecureStorage.getUser();

    if (!storedToken || !storedUser) {
      setIsLoading(false);
      return false;
    }

    try {
      // Validate token with server
      const response = await apiCall(ENDPOINTS.AUTH.USER, {}, storedToken);

      if (response.ok) {
        const data = await response.json();
        setToken(storedToken);
        setUser(data.user);
        resetSessionTimer();
        setIsLoading(false);
        return true;
      } else {
        // Token is invalid, clear storage
        SecureStorage.clearSession();
        setIsLoading(false);
        return false;
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear storage on network error
      SecureStorage.clearSession();
      setIsLoading(false);
      return false;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // apiCall() dispatches this the moment any request comes back 401 -- the
  // server-side token is already dead (expired/revoked). Locks the screen
  // in place rather than logging out, so a form the user was mid-way
  // through isn't lost; re-entering credentials in the lock overlay gets a
  // fresh token via the normal login() call.
  //
  // Gated on `token` because checkAuth() fires this same 401 path on the
  // *first* load of a tab that never had an authenticated session -- e.g. a
  // stale, days-old token left in localStorage from a previous visit, well
  // past the backend's 8-hour Knox TOKEN_TTL. Without this guard that dead
  // token produced the lock overlay instead of the normal login screen even
  // though nothing in this tab was ever "locked". Only lock when a session
  // was actually live (token already set in state) when it got invalidated.
  useEffect(() => {
    const handleSessionExpired = () => {
      if (!token) return; // no active session in this tab -- checkAuth's failure path already clears storage
      if (sessionLocked) return; // already locked, nothing to do
      lockScreen();
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [token, lockScreen, sessionLocked]);

  // Real inactivity tracking -- any interaction restarts the 30-minute
  // countdown. Previously resetSessionTimer() was only ever called at
  // login/checkAuth, so this was actually a flat 30-minute session length
  // rather than a true inactivity timer; a user working continuously past
  // 30 minutes would still have been locked out.
  useEffect(() => {
    if (!token || sessionLocked) return;
    const events: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    let throttled = false;
    const touchActivity = () => {
      if (throttled) return;
      throttled = true;
      window.setTimeout(() => { throttled = false; }, 10000); // at most once per 10s
      resetSessionTimer();
    };
    events.forEach(e => window.addEventListener(e, touchActivity, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, touchActivity));
  }, [token, sessionLocked, resetSessionTimer]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (sessionTimeoutRef.current) window.clearTimeout(sessionTimeoutRef.current);
      if (countdownRef.current) window.clearInterval(countdownRef.current);
    };
  }, []);

  const value = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    sessionTimeRemaining,
    sessionLocked,
    login,
    logout,
    checkAuth,
    resetSessionTimer,
    lockScreen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
